import { Config } from '@chegg-wt/core/config'
import { differenceInDays, format } from 'date-fns'
import { CITATION_LIST_TITLE_SINGLE } from '@chegg-wt/core/constants/citations'
import { htmlRegex } from '../constants/global'
import { SortKey, SortKeys } from '../constants/projects'
import { Citation } from '../types/redux-citation-api'
import { Project } from '../types/core'

const articleRegex = /^(THE|A|AN)\s/i
export const getVal = (obj: Citation, key: SortKey) =>
  key === SortKeys.ALPHABETICAL
    ? obj.formatted_data.output
        .toUpperCase()
        .replace(/['"“]/g, '')
        .replace(htmlRegex, '')
        .replace(articleRegex, '')
    : obj.date
export const getDir = (key: SortKey) => (key === SortKeys.DATE_MOST_RECENT ? 1 : -1)
/**
 * Sort citations based on:
 *
 * | sortKey         | prop                    | dir |
 * |-----------------|-------------------------|-----|
 * | alphabetical    | *.formatted_data.output | -1  |
 * | dateLeastRecent | *.date                  | -1  |
 * | dateMostRecent  | *.date                  |  1  |
 *
 */
export const sortCitationsByKey = (
  citations: Citation[],
  sortKey: SortKey = SortKeys.ALPHABETICAL
): Citation[] =>
  [...citations].sort((a, b) => {
    const valA = getVal(a, sortKey)
    const valB = getVal(b, sortKey)
    const dir = getDir(sortKey)
    if (valA < valB) return dir
    if (valA > valB) return -dir
    return 0
  })

/**
 * When retrieving data from EasyBib® API we sometimes need to merge the response
 * ```
 * // Fetched project data
 * projectData = {
 *   data: { id: '...', date: '...', ... },
 *   links: [...],
 * }
 * // After merging:
 * project = {
 *   id: '...',
 *   date: '...',
 *   links: [...],
 * }
 * ```
 * @param {Object} item - item to have its properties merged
 * @return {Object} object with merged properties
 */
export function mergeDataAndLinks(item) {
  const newItem = {
    links: {},
    ...item.data,
  }
  item.links.forEach((link) => {
    newItem.links[link.rel] = link.href
  })
  if (!newItem.id) {
    newItem.id = newItem.links.me
  }
  return newItem
}

/**
 * mergeDataAndLinks() for an array
 * @param {Array} list - list of items to get their properties merged
 * @return {Array} List of items with merged properties
 */
export function normalize(list) {
  return list.map((item) => mergeDataAndLinks(item))
}

export const addFields = (citations): Citation[] =>
  citations.map((citation) => ({
    ...citation,
    selected: false,
    isNew: false,
    isIncomplete: false,
    notes: [],
  }))

export const addProjectId = (projectId: string) => (citations: Citation[]) =>
  citations.map((citation) => ({ ...citation, projectId }))

/**
 * citationIdFromMeLink
 *
 * Assumes the meLink has the citationId stuck to the end of it; will return argument if not a valid meLink
 */
export const citationIdFromMeLink = (meLink: string) => {
  if (meLink && meLink.indexOf('/') > 0) {
    return meLink
      .split('/')
      .filter((segment) => segment !== '')
      .pop()
  }
  return meLink
}

export const hasExpired = (startAt: string): boolean =>
  !!Config.bibliographyExpirationInDays &&
  differenceInDays(Date.now(), new Date(startAt)) >= Config.bibliographyExpirationInDays

export const isExpiring = (startAt: string): boolean =>
  !!Config.bibliographyExpirationInDays &&
  differenceInDays(Date.now(), new Date(startAt)) < Config.bibliographyExpirationInDays
export const expireInDays = (startAt: string): number =>
  Config.bibliographyExpirationInDays - differenceInDays(Date.now(), new Date(startAt))

export const starMatchingProject = (
  projects: Project[],
  id: string,
  star: boolean
): Project[] => projects.map((p) => (p.id === id ? { ...p, starred: star } : p))

export const newProjectName = () =>
  `${CITATION_LIST_TITLE_SINGLE} ${format(new Date(), 'MM/dd/yyyy')}`
